import { Dispatch, SetStateAction, useCallback, useState } from 'react';

/**
 * `useEligibleItemsFilter` 가 다루는 query 의 최소 contract.
 *
 * 정산 기준일 변경 / 정산 완료일 변경 두 도메인이 모두 만족해야 하는 공통 필드만
 * 명시. 도메인별 dateFrom/To (baseDateFrom vs completeDateFrom 등) 차이는 generic
 * `Q` 로 흡수한다.
 */
export type EligibleItemsFilterContract = {
  page?: number;
  ids?: number[];
};

type UseEligibleItemsFilterResult<Q extends EligibleItemsFilterContract> = {
  /** 입력 중인 필터값. SearchBox 의 폼 컨트롤들이 양방향 바인딩. */
  draftFilter: Q;
  setDraftFilter: Dispatch<SetStateAction<Q>>;
  /** 실제 쿼리에 사용되는 필터값. `applyFilters` 호출 시에만 갱신된다. */
  appliedFilter: Q;
  setAppliedFilter: Dispatch<SetStateAction<Q>>;
  /**
   * 정산번호 입력용 raw 문자열. `draftFilter.ids`(number[]) 는 파싱 결과만 담으므로
   * 입력 중간의 콤마/공백이 제어형 재조합으로 사라지지 않도록 raw 를 별도 보관.
   */
  idsInputRaw: string;
  setIdsInputRaw: Dispatch<SetStateAction<string>>;
  /**
   * 검색 버튼이 한 번이라도 눌렸는지 여부.
   *
   * `false` 면 useQuery 의 `enabled` 를 끄는 용도 — 페이지 진입 시 자동 fetch 를
   * 방지해 비싼 join 쿼리가 즉시 발사되는 걸 막는다. 정산 변경 도메인의 eligible
   * items 쿼리는 10+ 테이블 join 으로 비용이 커서, 운영자가 검색 조건을 먼저 지정한
   * 뒤 명시적으로 조회를 시작하는 흐름이 자연스럽다.
   *
   * `applyFilters` 호출 시 `true`, `reset` 호출 시 `false`.
   */
  isApplied: boolean;
  /**
   * 검색 버튼/Enter 시 호출. idsInputRaw 를 파싱해 draft 에 반영하고
   * appliedFilter 를 page=1 로 갱신한다. `isApplied` 를 true 로 전환.
   */
  applyFilters: () => void;
  /** 초기 상태로 되돌림 (`isApplied=false`, 목록 다시 비어짐). */
  reset: () => void;
};

/** 정산번호 입력 문자열을 number[] 로 파싱. 무효값(빈칸/0/음수/비정수) 은 버린다. */
const parseIdsInput = (raw: string): number[] | undefined => {
  if (!raw.trim()) return undefined;
  const parsed = raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && Number.isInteger(n) && n > 0);
  return parsed.length > 0 ? parsed : undefined;
};

/**
 * 변경 대상 정산 아이템 검색 필터 state — 정산 기준일 / 정산 완료일 변경 도메인 공유.
 *
 * draft(편집 중) vs applied(쿼리에 사용) 분리 — SearchBox 컨벤션상
 * 검색 버튼을 누를 때만 applied 가 갱신되어 useQuery 재조회된다.
 *
 * 도메인별 차이는 `initialFilter` 의 `statuses` default 정도 — 호출처가 모듈 스코프
 * 상수로 전달한다. (`reset` 의 deps 에 들어가므로 매 렌더마다 새 객체면 안 됨.)
 *
 * @example
 * // 모듈 스코프 상수 — stable reference 보장
 * const INITIAL_FILTER: EligibleItemsQuery = {
 *   page: 1, pageSize: 50,
 *   statuses: ['completed' as SettlementItemStatus],
 *   periodTypes: ['daily' as SettlementItemPeriodType],
 * };
 * const filter = useEligibleItemsFilter<EligibleItemsQuery>(INITIAL_FILTER);
 */
export const useEligibleItemsFilter = <Q extends EligibleItemsFilterContract>(
  initialFilter: Q,
): UseEligibleItemsFilterResult<Q> => {
  const [draftFilter, setDraftFilter] = useState<Q>(initialFilter);
  const [appliedFilter, setAppliedFilter] = useState<Q>(initialFilter);
  const [idsInputRaw, setIdsInputRaw] = useState<string>('');
  // 검색 버튼이 눌리기 전까지는 eligibleQuery 가 발동하지 않도록 가드.
  const [isApplied, setIsApplied] = useState<boolean>(false);

  const applyFilters = useCallback((): void => {
    // 정산번호 raw 입력을 지금 시점에 파싱해서 draft 에 확정 반영.
    // onChange 에서 즉시 파싱하면 "535355," 같은 중간 입력이 제어형 재조합으로 깨진다.
    const parsedIds = parseIdsInput(idsInputRaw);
    setDraftFilter((prev) => ({ ...prev, ids: parsedIds }));
    setAppliedFilter({ ...draftFilter, ids: parsedIds, page: 1 });
    setIsApplied(true);
  }, [draftFilter, idsInputRaw]);

  const reset = useCallback((): void => {
    setDraftFilter(initialFilter);
    setAppliedFilter(initialFilter);
    setIdsInputRaw('');
    setIsApplied(false);
  }, [initialFilter]);

  return {
    draftFilter,
    setDraftFilter,
    appliedFilter,
    setAppliedFilter,
    idsInputRaw,
    setIdsInputRaw,
    isApplied,
    applyFilters,
    reset,
  };
};
